// ─────────────────────────────────────────────────────────────────────────────
// BirdSpawnerManager
//
// Lê as regras de pássaros do JSON, escolhe uma borda aleatória da tela (ou um
// ponto fora do mapa) e instancia o pássaro com velocidade e direção corretas.
// Cada pássaro voa em linha + curva senoidal + ondulação de altitude, e é
// DESTRUÍDO automaticamente ao sair da área visível (despawn).
//
// Totalmente independente do jogador. O renderer só precisa iterar `birds`.
// ─────────────────────────────────────────────────────────────────────────────

import { BirdSpawnerConfig, EagleConfig, SpawnEdge, Viewport } from './schema';

let SEQ = 0;

export interface Bird {
  id: number;
  /** Posição na tela (px, mesma escala do Viewport). */
  x: number;
  y: number;
  /** Velocidade instantânea (px/s) — já inclui curva; útil para orientar o sprite. */
  vx: number;
  vy: number;
  /** Direção de voo base (rad). */
  heading: number;
  speed: number;
  /** Segundos desde o spawn (para as senoides e o flap). */
  age: number;
  /** Deslocamento vertical fixo sorteado (px). */
  baseAltitude: number;
  /** Fases aleatórias para dessincronizar os pássaros entre si. */
  swayPhase: number;
  bobPhase: number;
  scale: number;
  /** Fração 0..1 do frame de batida de asa (procedural) / troca de frame (sheet). */
  flap: number;
}

/**
 * Águia caçadora: mergulha numa presa (persegue a posição atual dela), agarra
 * e sai da tela carregando. A presa some da lista de pássaros ao ser pega.
 */
export interface Eagle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  phase: 'dive' | 'carry';
  /** id do pássaro perseguido (fase dive). */
  targetId: number | null;
  /** presa nas garras (fase carry) — só para desenho. */
  prey: Bird | null;
  age: number;
  flap: number;
  scale: number;
}

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const randInt = (a: number, b: number) => Math.floor(rand(a, b + 1));
const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const deg2rad = (d: number) => (d * Math.PI) / 180;

export class BirdSpawnerManager {
  readonly id: string;
  private cfg: BirdSpawnerConfig;
  private birds: Bird[] = [];
  private spawnTimer: number;
  private eagles: Eagle[] = [];
  private huntTimer: number;

  constructor(id: string, cfg: BirdSpawnerConfig) {
    this.id = id;
    this.cfg = cfg;
    this.spawnTimer = this.nextInterval();
    this.huntTimer = this.nextHunt();
  }

  private nextHunt(): number {
    const e = this.cfg.eagle;
    return e ? rand(e.intervalMinSec, e.intervalMaxSec) : Infinity;
  }

  /** Águias em cena (somente leitura para o renderer). */
  getEagles(): readonly Eagle[] {
    return this.eagles;
  }

  /**
   * Solta uma águia atrás de um pássaro vivo agora (ignora intervalo e chance).
   * Devolve false se não há pássaro no céu. Usado pelo sorteio e por testes.
   */
  startHunt(vp: Viewport): boolean {
    const e: EagleConfig | undefined = this.cfg.eagle;
    if (!e || this.birds.length === 0) return false;
    const target = pick(this.birds);
    // Entra pelo canto de cima mais distante da presa (mergulho em diagonal).
    const fromLeft = target.x > vp.width / 2;
    const margin = this.cfg.edgeMarginPx;
    this.eagles.push({
      x: fromLeft ? -margin : vp.width + margin,
      y: -margin,
      vx: 0, vy: 0,
      phase: 'dive',
      targetId: target.id,
      prey: null,
      age: 0,
      flap: 0,
      scale: e.scale,
    });
    return true;
  }

  private updateEagles(dt: number, vp: Viewport): void {
    const e = this.cfg.eagle;
    if (!e) return;
    if (e.enabled && this.eagles.length === 0) {
      this.huntTimer -= dt;
      if (this.huntTimer <= 0) {
        this.huntTimer = this.nextHunt();
        if (Math.random() < e.chance) this.startHunt(vp);
      }
    }

    const margin = this.cfg.edgeMarginPx;
    for (let i = this.eagles.length - 1; i >= 0; i--) {
      const g = this.eagles[i];
      g.age += dt;
      g.flap = (g.flap + dt * 5) % 1; // bate asa devagar (planador)

      if (g.phase === 'dive') {
        let target = this.birds.find(b => b.id === g.targetId);
        if (!target && this.birds.length > 0) { // a presa saiu da tela: escolhe outra
          target = this.birds.reduce((a, b) => (Math.hypot(a.x - g.x, a.y - g.y) < Math.hypot(b.x - g.x, b.y - g.y) ? a : b));
          g.targetId = target.id;
        }
        if (!target) { // céu vazio: desiste e sobe
          g.phase = 'carry';
        } else {
          const dx = target.x - g.x, dy = target.y - g.y;
          const d = Math.hypot(dx, dy);
          if (d < 16 * g.scale) { // pegou!
            this.birds.splice(this.birds.indexOf(target), 1);
            g.prey = target;
            g.phase = 'carry';
          } else {
            g.vx = (dx / d) * e.speedPxPerSec;
            g.vy = (dy / d) * e.speedPxPerSec;
          }
        }
      }
      if (g.phase === 'carry') {
        // sai pelo lado em que já está indo, subindo
        const dirX = g.vx >= 0 ? 1 : -1;
        const sp = e.speedPxPerSec * 0.7;
        g.vx = dirX * sp * 0.85;
        g.vy = -sp * 0.5;
      }
      g.x += g.vx * dt;
      g.y += g.vy * dt;

      const out = g.x < -margin * 1.5 || g.x > vp.width + margin * 1.5 || g.y < -margin * 1.5 || g.y > vp.height + margin * 1.5;
      if (out && g.age > 1) this.eagles.splice(i, 1);
    }
  }

  private nextInterval(): number {
    return rand(this.cfg.spawnIntervalMin, this.cfg.spawnIntervalMax);
  }

  /** Lista viva (somente leitura para o renderer). */
  getBirds(): readonly Bird[] {
    return this.birds;
  }

  /** Remove todos (ex.: ao trocar de mapa). */
  clear(): void {
    this.birds.length = 0;
    this.eagles.length = 0;
  }

  /** Liga/desliga o surgimento de novos pássaros (os já vivos continuam). */
  setEnabled(on: boolean): void {
    this.cfg = { ...this.cfg, enabled: on };
  }

  /**
   * Avança o spawner e todos os pássaros em `dt` segundos.
   * `vp` é a área visível ATUAL (px) — usada tanto para nascer nas bordas
   * quanto para destruir quem saiu.
   */
  update(dt: number, vp: Viewport): void {
    if (this.cfg.enabled) {
      this.spawnTimer -= dt;
      if (this.spawnTimer <= 0) {
        this.spawnTimer = this.nextInterval();
        const burst = randInt(this.cfg.spawnBurstMin, this.cfg.spawnBurstMax);
        for (let i = 0; i < burst && this.birds.length < this.cfg.maxAlive; i++) {
          this.birds.push(this.spawnOne(vp));
        }
      }
    }

    const { altitude, curve } = this.cfg.flight;
    const swayW = (Math.PI * 2) / Math.max(0.01, curve.swayPeriodSec);
    const bobW = (Math.PI * 2) / Math.max(0.01, altitude.bobPeriodSec);
    const margin = this.cfg.edgeMarginPx;

    for (let i = this.birds.length - 1; i >= 0; i--) {
      const b = this.birds[i];
      b.age += dt;

      // deslocamento base (linha reta na direção `heading`)
      const fwdX = Math.cos(b.heading);
      const fwdY = Math.sin(b.heading);
      // perpendicular (para a curva senoidal lateral)
      const perpX = -fwdY;
      const perpY = fwdX;

      // velocidade da curva = derivada do desvio lateral senoidal
      const swayVel = curve.swayAmplitudePx * swayW * Math.cos(b.age * swayW + b.swayPhase);
      // velocidade da ondulação de altitude
      const bobVel = altitude.bobAmplitudePx * bobW * Math.cos(b.age * bobW + b.bobPhase);

      b.vx = fwdX * b.speed + perpX * swayVel;
      b.vy = fwdY * b.speed + perpY * swayVel + bobVel;

      b.x += b.vx * dt;
      b.y += b.vy * dt;

      b.flap = (b.flap + dt * (this.cfg.sprite.flapFps ?? 12)) % 1;

      // despawn ao sair da área visível + margem
      const outX = b.x < -margin || b.x > vp.width + margin;
      const outY = b.y < -margin || b.y > vp.height + margin;
      // só destrói depois de ter tido chance de entrar (evita matar no frame 0)
      if ((outX || outY) && b.age > 0.5) {
        this.birds.splice(i, 1);
      }
    }

    this.updateEagles(dt, vp);
  }

  /** Cria um pássaro numa borda sorteada, voando para atravessar a tela. */
  private spawnOne(vp: Viewport): Bird {
    const f = this.cfg.flight;
    const margin = this.cfg.edgeMarginPx;
    const edge: SpawnEdge = pick(this.cfg.edges);

    let x = 0;
    let y = 0;
    let heading = 0;

    switch (edge) {
      case 'N': // topo, voando para baixo (com desvio horizontal)
        x = rand(0, vp.width);
        y = -margin;
        heading = deg2rad(rand(45, 135)); // entre ↘ e ↙
        break;
      case 'S':
        x = rand(0, vp.width);
        y = vp.height + margin;
        heading = deg2rad(rand(-135, -45)); // entre ↗ e ↖
        break;
      case 'W':
        x = -margin;
        y = rand(0, vp.height);
        heading = deg2rad(rand(-45, 45)); // para a direita
        break;
      case 'E':
        x = vp.width + margin;
        y = rand(0, vp.height);
        heading = deg2rad(rand(135, 225)); // para a esquerda
        break;
      case 'random_offscreen':
      default: {
        // ponto qualquer fora da tela, voando grosso modo em direção ao centro
        const side = randInt(0, 3);
        if (side === 0) { x = rand(-margin, vp.width + margin); y = -margin; }
        else if (side === 1) { x = vp.width + margin; y = rand(-margin, vp.height + margin); }
        else if (side === 2) { x = rand(-margin, vp.width + margin); y = vp.height + margin; }
        else { x = -margin; y = rand(-margin, vp.height + margin); }
        heading = Math.atan2(vp.height / 2 - y, vp.width / 2 - x);
        break;
      }
    }

    // jitter no ângulo de voo
    heading += deg2rad(rand(-f.headingJitterDeg, f.headingJitterDeg));

    // deslocamento vertical fixo sorteado por pássaro (além da trajetória base)
    const baseAltitude = rand(f.altitude.baseMinPx, f.altitude.baseMaxPx);
    y += baseAltitude;

    return {
      id: SEQ++,
      x, y,
      vx: 0, vy: 0,
      heading,
      speed: rand(f.speedMinPxPerSec, f.speedMaxPxPerSec),
      age: 0,
      baseAltitude,
      swayPhase: rand(0, Math.PI * 2),
      bobPhase: rand(0, Math.PI * 2),
      scale: this.cfg.sprite.scale ?? 1,
      flap: Math.random(),
    };
  }
}
